/** Takes a class instance and turns it into a plain object that can be stored in React's state. */
export function makePlainObject<T extends object>(instance: T)
{
    const ret = {} as any;
    Object.assign(ret, instance);    
    return ret as T;
}

export function deepCopyObject<T extends object>(obj: T)
{
    const str = JSON.stringify(obj);
    obj = JSON.parse(str);
    return obj;
}

/**
 * Sorts the array according to the property computed by the callback.
 * Returns a new array.
 * The original array is not modified.
 */
export function orderBy<T>(items: T[], callback: (item: T) => any)
{
    if (!items)
    {
        return [];
    }

    return items.slice().sort(function(a, b)
    {
        const c = callback(a);
        const d = callback(b);
        
        if (c < d) return -1;
        if (c > d) return 1;
        return 0;
    });
}

/**
 * Group items by custom keys.
 * @param items An array.
 * @param keyCallback A function that computes the key. The key must be a type that can be used to index an object.
 * @param sortCallback An optional sort function. If provided, each group will be sorted by this function.
 * @returns A map from keys to sub-arrays of items.
 */
export function groupBy<T, U extends string | number | symbol>(
    items: T[],
    keyCallback: (value: T) => U,
    sortCallback?: (item: T) => any
)
{
    const ret = {} as {[K in U]: T[]};

    if (!items)
    {
        return ret;
    }
    if (sortCallback)
    {
        items = orderBy(items, sortCallback);
    }

    for (const val of items)
    {
        const key = keyCallback(val);
        ret[key] = ret[key] ?? [];
        ret[key].push(val);
    }
    return ret;
}

/** Turns an array into a dictionary. */
export function toDict<T, U extends string | number | symbol, V>(
    array: T[],
    keyCallback: (value: T) => U,
    valueCallback: (value: T) => V
)
{
    const ret = {} as {[K in U]: V};

    for (const x of array)
    {
        const key = keyCallback(x);
        const value = valueCallback(x);
        ret[key] = value;
    }

    return ret;
}

/** Escapes a string for use as a regex by prefixing special characters with a backslash. */
export function escapeRegExp(pattern: string)
{
    //The first argument is a list of special characters.
    //The second is its replacement. \\ is the backslash, and $& means the matched substring.
    //The list of special characters comes from the documentation for PHP's preg_quote.
    //However, two characters in that list - ":" and "!" - actually cause trouble if they're escaped.
    return pattern.replace(/[.\\+*?[^\]$(){}=<>|\-#]/g, '\\$&');
}